import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { CrmOpportunityStage } from "@enums/crmOpportunityStage.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { CrmLead } from "@interfaces/models/crmLead.interface";
import { CrmOpportunity } from "@interfaces/models/crmOpportunity.interface";
import { CrmTask } from "@interfaces/models/crmTask.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { CrmLeadService } from "@services/crmLead.service";
import { CrmOpportunityService } from "@services/crmOpportunity.service";
import { CrmTaskService } from "@services/crmTask.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "crm-opportunity-pipeline",
	styleUrls: ["crmOpportunityPipeline.css"],
	templateUrl: "crmOpportunityPipeline.html"
})
export class CrmOpportunityPipelineAdminComponent {
	addTaskNewTask: CrmTask = new CrmTask();
	addTaskOpportunityId: number | null = null;
	addTaskVisible = false;
	columns: { items: CrmOpportunity[]; stage: any }[] = [];
	expandedCardId: number | null = null;
	filterAssignedUserId: number | null = null;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	items: CrmOpportunity[] = [];
	leads: CrmLead[] = [];
	searchText = "";
	stages: any[] = [];
	tasksByOpportunity: { [key: number]: CrmTask[] } = {};
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private crmTaskService: CrmTaskService, private itemService: CrmOpportunityService, private crmLeadService: CrmLeadService, private userProfileService: UserProfileService) {
		this.stages = GetPropertiesFromEnum(CrmOpportunityStage);
		this.headerPrimaryButtons = [
			{ icon: "fal fa-plus", method: "newOpportunity", text: "New Opportunity" },
			{ icon: "fal fa-list", method: "listView", text: "List View" }
		];
		this.getItems();
		this.getLeads();
		this.getUserProfiles();
	}

	addTask(oppId: number, event: Event) {
		event.stopPropagation();
		this.addTaskOpportunityId = oppId;
		this.addTaskNewTask = new CrmTask();
		this.addTaskVisible = true;
	}

	buildColumns() {
		let filtered = this.items;
		if (this.searchText) {
			const q = this.searchText.toLowerCase();
			filtered = filtered.filter(o =>
				o.title?.toLowerCase().includes(q) ||
				this.getLeadName(o.crmLeadId)?.toLowerCase().includes(q)
			);
		}
		if (this.filterAssignedUserId) {
			filtered = filtered.filter(o => o.assignedUserId === this.filterAssignedUserId);
		}
		this.columns = this.stages.map(s => ({
			items: filtered.filter(o => o.stage === s.id),
			stage: s
		}));
	}

	closeAddTask() {
		this.addTaskVisible = false;
		this.addTaskOpportunityId = null;
	}

	getAssignedName(userProfileId: number | undefined): string {
		if (!userProfileId) return "";
		return this.userProfiles.find(u => u.id === userProfileId)?.fullName ?? "";
	}

	getItems() {
		this.itemService.getAll().subscribe(
			(res: any) => { this.items = res.response.items; this.buildColumns(); },
			() => { this.toastService.error("Something Went Wrong!"); }
		);
	}

	getLeadName(leadId?: number): string {
		if (!leadId) return "";
		const lead = this.leads.find(l => l.id === leadId);
		return lead ? lead.companyName : "";
	}

	getLeads() {
		this.crmLeadService.getAll().subscribe(
			(res: any) => { this.leads = (res.response.items || []).filter((l: any) => l.isActive); }
		);
	}

	getStageColor(stageId: number): string {
		const colors: { [key: number]: string } = {
			0: "#0d6efd",
			10: "#6f42c1",
			20: "#0dcaf0",
			25: "#ffc107",
			27: "#20c997",
			30: "#fd7e14",
			40: "#198754",
			50: "#dc3545",
			60: "#6c757d"
		};
		return colors[stageId] || "#dee2e6";
	}

	getStageName(stageId: number): string {
		const names: { [key: number]: string } = {
			0: "Awaiting RFQ",
			10: "Quoting",
			20: "Quoted",
			25: "Needs Chasing",
			27: "Chased",
			30: "Negotiation",
			40: "Closed Won",
			50: "Closed Lost",
			60: "On Hold"
		};
		return names[stageId] ?? "";
	}

	getUserProfiles() {
		this.userProfileService.getAll().subscribe(
			(res: any) => { this.userProfiles = res.response.items; }
		);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	listView() { this.router.navigate(["/admin/crmOpportunities"]); }

	loadTasks(oppId: number) {
		this.crmTaskService.getByOpportunityId(oppId).subscribe(
			(res: any) => { this.tasksByOpportunity[oppId] = res.response || []; },
			() => { this.tasksByOpportunity[oppId] = []; }
		);
	}

	newOpportunity() { this.router.navigate(["/admin/crmOpportunities/create"]); }

	onCardDrop(e: any) {
		const item: CrmOpportunity = e.fromData.items.splice(e.fromIndex, 1)[0];
		item.stage = e.toData.stage.id;
		if (item.stage === CrmOpportunityStage.ClosedWon || item.stage === CrmOpportunityStage.ClosedLost) {
			item.actualCloseDate = new Date();
		} else {
			item.actualCloseDate = undefined;
		}
		e.toData.items.splice(e.toIndex, 0, item);
		this.itemService.updateSingleById(item.id, item).subscribe(
			() => {
				this.toastService.success("Stage updated!");
				if (item.stage === CrmOpportunityStage.ClosedWon) { this.showConfetti(); }
				if (item.stage === CrmOpportunityStage.ClosedLost) { this.showLostPulse(); }
				if (item.stage === CrmOpportunityStage.Quoted) { this.showPaperPlane(); }
			},
			() => { this.toastService.error("Failed to update stage"); this.getItems(); }
		);
	}

	onCardReorder(e: any) {
		const item = e.fromData.items.splice(e.fromIndex, 1)[0];
		e.fromData.items.splice(e.toIndex, 0, item);
	}

	saveAddTask() {
		if (!this.addTaskOpportunityId || !this.addTaskNewTask.title) { this.toastService.error("Title is required."); return; }
		this.addTaskNewTask.crmOpportunityId = this.addTaskOpportunityId;
		this.crmTaskService.createSingle(this.addTaskNewTask).subscribe(
			() => {
				this.toastService.success("Task added!");
				this.loadTasks(this.addTaskOpportunityId!);
				this.closeAddTask();
			},
			() => { this.toastService.error("Something Went Wrong!"); }
		);
	}

	showConfetti() {
		const container = document.createElement("div");
		container.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden";
		document.body.appendChild(container);
		const colors = ["#198754", "#0d6efd", "#ffc107", "#dc3545", "#6f42c1", "#0dcaf0", "#fd7e14"];
		for (let i = 0; i < 150; i++) {
			const piece = document.createElement("div");
			const color = colors[Math.floor(Math.random() * colors.length)];
			const left = Math.random() * 100;
			const delay = Math.random() * 2;
			const duration = 2 + Math.random() * 2;
			const size = 6 + Math.random() * 6;
			const rotation = Math.random() * 360;
			piece.style.cssText = `position:absolute;top:-20px;left:${left}%;width:${size}px;height:${size * 0.6}px;background:${color};opacity:0.9;border-radius:2px;transform:rotate(${rotation}deg);animation:confetti-fall ${duration}s ease-in ${delay}s forwards`;
			container.appendChild(piece);
		}
		if (!document.querySelector("#confetti-style")) {
			const style = document.createElement("style");
			style.id = "confetti-style";
			style.textContent = `@keyframes confetti-fall{0%{top:-20px;opacity:1;transform:rotate(0deg) translateX(0)}50%{opacity:1}100%{top:110vh;opacity:0;transform:rotate(720deg) translateX(${Math.random() > 0.5 ? "" : "-"}80px)}}`;
			document.head.appendChild(style);
		}
		setTimeout(() => container.remove(), 5000);
	}

	showLostPulse() {
		const overlay = document.createElement("div");
		overlay.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none";
		const vignette = document.createElement("div");
		vignette.style.cssText = "position:absolute;inset:0;background:radial-gradient(ellipse at center,transparent 30%,rgba(180,0,0,0.7) 100%);animation:lost-pulse 0.6s ease-out 3;opacity:0";
		overlay.appendChild(vignette);
		document.body.appendChild(overlay);
		if (!document.querySelector("#lost-pulse-style")) {
			const style = document.createElement("style");
			style.id = "lost-pulse-style";
			style.textContent = "@keyframes lost-pulse{0%{opacity:0}30%{opacity:1}100%{opacity:0}}";
			document.head.appendChild(style);
		}
		setTimeout(() => overlay.remove(), 2000);
	}

	showPaperPlane() {
		const overlay = document.createElement("div");
		overlay.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden";
		document.body.appendChild(overlay);
		const planes = [
			{ color: "#0dcaf0", delay: 0, size: 3, startTop: 75 },
			{ color: "#6f42c1", delay: 0.4, size: 2.2, startTop: 55 },
			{ color: "#0d6efd", delay: 0.9, size: 2.6, startTop: 65 },
		];
		for (const p of planes) {
			const plane = document.createElement("div");
			plane.innerHTML = `<i class="fas fa-paper-plane" style="font-size:${p.size}rem;color:${p.color};filter:drop-shadow(0 2px 8px rgba(0,0,0,0.3))"></i>`;
			plane.style.cssText = `position:absolute;top:${p.startTop}%;left:-60px;animation:plane-fly 3.5s ease-in-out ${p.delay}s forwards`;
			overlay.appendChild(plane);
		}
		if (!document.querySelector("#plane-style")) {
			const style = document.createElement("style");
			style.id = "plane-style";
			style.textContent = "@keyframes plane-fly{0%{left:-60px;top:75%;transform:rotate(-15deg)}25%{top:35%;transform:rotate(-25deg)}50%{top:45%;transform:rotate(-10deg)}75%{top:25%;transform:rotate(-20deg)}100%{left:110%;top:10%;transform:rotate(-18deg)}}";
			document.head.appendChild(style);
		}
		setTimeout(() => overlay.remove(), 5000);
	}

	toggleCardExpand(oppId: number, event: Event) {
		event.stopPropagation();
		if (this.expandedCardId === oppId) {
			this.expandedCardId = null;
		} else {
			this.expandedCardId = oppId;
			if (!this.tasksByOpportunity[oppId]) { this.loadTasks(oppId); }
		}
	}

	toggleTaskComplete(task: CrmTask, event: Event) {
		event.stopPropagation();
		const updated = { ...task, completedDate: !task.isCompleted ? new Date() : null, isCompleted: !task.isCompleted };
		this.crmTaskService.updateSingleById(task.id, updated).subscribe(
			() => { if (task.crmOpportunityId) { this.loadTasks(task.crmOpportunityId); } },
			() => { this.toastService.error("Something Went Wrong!"); }
		);
	}

	viewOpportunity(id: number) { this.router.navigate(["/admin/crmOpportunities", id]); }
}
